import React, { useEffect, useMemo, useState } from "react";
import {
  Activity, AlertTriangle, Download, Eye, Globe, MonitorSmartphone, Radio, Users, UserPlus, Timer,
} from "@/lib/icons";
import {
  Area, AreaChart, Bar, BarChart, CartesianGrid,
  ResponsiveContainer, Tooltip, XAxis, YAxis,
} from "recharts";
import { api } from "@/lib/api";
import { useTheme } from "@/lib/theme";

const panelClass = "admin-surface";
const compactNumber = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  const number = Number(value);
  return Number.isFinite(number) ? number.toLocaleString("en-IN") : "-";
};
const shortDate = (value) => new Intl.DateTimeFormat("en-IN", { day: "numeric", month: "short" }).format(new Date(value));
const formatDuration = (seconds) => {
  const s = Math.round(Number(seconds) || 0);
  return s >= 60 ? `${Math.floor(s / 60)}m ${s % 60}s` : `${s}s`;
};
const formatPercent = (ratio) => `${Math.round((Number(ratio) || 0) * 100)}%`;

function MetricCard({ icon: Icon, label, value, sub, testid }) {
  return (
    <div className="admin-metric-card" data-testid={testid}>
      <span className="admin-metric-icon"><Icon size={15} /></span>
      <div className="admin-metric-value tabular-nums">{value ?? "-"}</div>
      <div className="admin-metric-label">{label}</div>
      {sub && <div className="mt-0.5 text-[11px] text-white/30">{sub}</div>}
    </div>
  );
}

function RankedList({ title, items, valueLabel, emptyLabel }) {
  const max = Math.max(...items.map((i) => i.value), 1);
  return (
    <section className={`${panelClass} p-5`}>
      <h3 className="font-display text-lg text-white">{title}</h3>
      <div className="mt-5 space-y-4">
        {items.map((item) => (
          <div key={item.name}>
            <div className="mb-1.5 flex items-center justify-between gap-4 text-xs">
              <span className="truncate text-white/70">{item.name || "(not set)"}</span>
              <span className="tabular-nums text-white/45">{compactNumber(item.value)} {valueLabel}</span>
            </div>
            <div className="h-1.5 overflow-hidden rounded-full bg-white/5">
              <div className="h-full rounded-full bg-[#9D00FF]" style={{ width: `${Math.max(4, (item.value / max) * 100)}%` }} />
            </div>
          </div>
        ))}
        {!items.length && <p className="py-8 text-center text-sm text-white/35">{emptyLabel}</p>}
      </div>
    </section>
  );
}

export default function AdminAnalytics() {
  const { theme } = useTheme();
  const [status, setStatus] = useState(null);
  const [realtime, setRealtime] = useState(null);
  const [overview, setOverview] = useState(null);
  const [topPages, setTopPages] = useState(null);
  const [sources, setSources] = useState(null);
  const [devices, setDevices] = useState(null);
  const [geo, setGeo] = useState(null);
  const [regions, setRegions] = useState(null);
  const [days, setDays] = useState(28);
  const [siteActivity, setSiteActivity] = useState(null);

  useEffect(() => {
    api.get("/admin/analytics/ga4/status").then((r) => setStatus(r.data)).catch(() => setStatus({ configured: false }));
  }, []);

  // Operational counters such as invoice downloads come from PRONET's own
  // event log and load independently of GA4.
  useEffect(() => {
    api.get("/admin/analytics").then((r) => setSiteActivity(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!status?.configured) return;
    const loadRealtime = () => api.get("/admin/analytics/ga4/realtime").then((r) => setRealtime(r.data)).catch(() => {});
    loadRealtime();
    const id = setInterval(loadRealtime, 30000);
    return () => clearInterval(id);
  }, [status?.configured]);

  useEffect(() => {
    if (!status?.configured) return;
    Promise.allSettled([
      api.get(`/admin/analytics/ga4/overview?days=${days}`),
      api.get(`/admin/analytics/ga4/top-pages?days=${days}`),
      api.get(`/admin/analytics/ga4/traffic-sources?days=${days}`),
      api.get(`/admin/analytics/ga4/devices?days=${days}`),
      api.get(`/admin/analytics/ga4/geo?days=${days}`),
      api.get(`/admin/analytics/ga4/regions?days=${days}`),
    ]).then(([o, p, s, d, g, rg]) => {
      setOverview(o.status === "fulfilled" ? o.value.data : null);
      setTopPages(p.status === "fulfilled" ? p.value.data : null);
      setSources(s.status === "fulfilled" ? s.value.data : null);
      setDevices(d.status === "fulfilled" ? d.value.data : null);
      setGeo(g.status === "fulfilled" ? g.value.data : null);
      setRegions(rg.status === "fulfilled" ? rg.value.data : null);
    });
  }, [status?.configured, days]);

  const chartLine = theme === "light" ? "rgba(32,23,43,.22)" : "rgba(255,255,255,.25)";
  const chartGrid = theme === "light" ? "rgba(32,23,43,.08)" : "rgba(255,255,255,.06)";
  const chartTooltip = {
    contentStyle: {
      background: theme === "light" ? "#ffffff" : "#171720",
      border: `1px solid ${theme === "light" ? "rgba(32,23,43,.14)" : "rgba(255,255,255,.12)"}`,
      borderRadius: 12,
      color: theme === "light" ? "#20172b" : "#ffffff",
      fontSize: 12,
    },
    cursor: { fill: "rgba(157,0,255,.08)" },
  };

  const topPagesRanked = useMemo(
    () => (topPages?.items || []).map((i) => ({ name: i.path, value: i.views })),
    [topPages],
  );
  const sourcesRanked = useMemo(
    () => (sources?.items || []).map((i) => ({ name: i.channel, value: i.sessions })),
    [sources],
  );
  const deviceData = useMemo(
    () => (devices?.items || []).map((i) => ({ name: i.device, value: i.users })),
    [devices],
  );
  const geoRanked = useMemo(
    () => (geo?.items || []).map((i) => ({ name: i.country, value: i.users })),
    [geo],
  );
  const regionsRanked = useMemo(
    () => (regions?.items || []).map((i) => ({ name: i.state, value: i.users })),
    [regions],
  );
  // Members-by-state/district come from our own member records (admin_analytics),
  // not GA4 — GA4 has no India-district geo dimension, so this is the only
  // place district-level breakdown can come from.
  const membersByState = useMemo(
    () => (siteActivity?.members_by_state || []).map((i) => ({ name: i.name, value: i.count })),
    [siteActivity],
  );
  const membersByDistrict = useMemo(
    () => (siteActivity?.members_by_district || []).map((i) => ({ name: i.name, value: i.count })),
    [siteActivity],
  );
  const invoiceDownloads = siteActivity?.invoice_downloads;
  const invoiceBreakdown = invoiceDownloads
    ? [
        `${compactNumber(invoiceDownloads.tracked)} tracked`,
        invoiceDownloads.historical_inferred > 0
          ? `${compactNumber(invoiceDownloads.historical_inferred)} inferred from older sent/paid invoices`
          : null,
        invoiceDownloads.unattributed > 0
          ? `${compactNumber(invoiceDownloads.unattributed)} legacy count${invoiceDownloads.unattributed === 1 ? "" : "s"}`
          : null,
      ].filter(Boolean).join(" · ")
    : "PRONET data unavailable";

  if (status === null) {
    return <div className="py-16 text-white/50">Loading analytics…</div>;
  }

  return (
    <div className="space-y-8">
      <header className="admin-page-heading fade-up">
        <div>
          <div className="admin-page-kicker">Data & insights</div>
          <h1 className="mt-2 font-display text-4xl text-white sm:text-5xl">Analytics</h1>
          <p className="admin-page-description">Google Analytics traffic and PRONET operational activity in one clear view.</p>
        </div>
        {status.configured && (
          <div className="inline-flex w-fit rounded-xl border border-white/10 bg-black/20 p-1" aria-label="Date range">
            {[7, 28, 90].map((d) => (
              <button key={d} type="button" data-testid={`ga4-range-${d}`} onClick={() => setDays(d)}
                className={`rounded-lg px-3 py-2 text-xs font-semibold transition ${days === d ? "bg-[#9D00FF] text-white" : "text-white/50 hover:text-white"}`}>
                {d}d
              </button>
            ))}
          </div>
        )}
      </header>

      <section data-testid="analytics-overview">
        <h2 className="admin-section-heading">Analytics overview</h2>
        <p className="admin-section-copy">Traffic comes from GA4 for the selected period; invoice downloads are recorded directly by PRONET.</p>
        <div className="mt-4 grid grid-cols-2 gap-3 md:grid-cols-3">
          <MetricCard icon={Eye} label={`Pageviews · last ${days}d`} value={compactNumber(overview?.totals?.pageviews)} sub={status.configured ? "Google Analytics" : "GA4 reporting unavailable"} testid="overview-pageviews" />
          <MetricCard icon={Users} label={`Users · last ${days}d`} value={compactNumber(overview?.totals?.users)} sub={status.configured ? "Google Analytics" : "GA4 reporting unavailable"} testid="overview-users" />
          <MetricCard icon={Download} label="Invoice downloads · all time" value={compactNumber(invoiceDownloads?.total)} sub={invoiceBreakdown} testid="site-invoice-downloads" />
        </div>
      </section>

      <div className="grid gap-4 xl:grid-cols-2">
        <RankedList title="Members by state" items={membersByState} valueLabel="members" emptyLabel="State breakdown will appear here." />
        <RankedList title="Members by district" items={membersByDistrict} valueLabel="members" emptyLabel="District breakdown will appear here." />
      </div>

      {!status.configured ? (
        <section className={`${panelClass} p-8`} data-testid="ga4-not-configured">
          <div className="flex items-start gap-3">
            <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-amber-400/30 bg-amber-400/10">
              <AlertTriangle size={18} className="text-amber-300" />
            </span>
            <div>
              <h2 className="font-display text-xl text-white">
                {status.tracking_configured ? "Website tracking is connected" : "GA4 isn't connected yet"}
              </h2>
              <p className="mt-2 max-w-2xl text-sm leading-relaxed text-white/55">
                {status.tracking_configured && (
                  <span className="mb-2 block text-emerald-300/90">
                    Visitor events are being sent through <span className="kbd">{status.measurement_id}</span>.
                  </span>
                )}
                The charts on this page additionally require <span className="kbd">GA4_PROPERTY_ID</span> and valid
                server-side reporting credentials, with Viewer access
                for the service account on the GA4 property.
                {status.error && <span className="mt-2 block text-amber-300/80">{status.error}</span>}
              </p>
            </div>
          </div>
        </section>
      ) : (
        <>
          <div>
            <h2 className="admin-section-heading">Google Analytics</h2>
            <p className="admin-section-copy">Audience and engagement for the selected reporting period.</p>
          </div>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            <MetricCard icon={Radio} label="Active right now" value={compactNumber(realtime?.active_users)} sub="Realtime, GA4" testid="ga4-active-now" />
            <MetricCard icon={Activity} label={`Sessions · last ${days}d`} value={compactNumber(overview?.totals?.sessions)} testid="ga4-sessions" />
            <MetricCard icon={UserPlus} label={`New users · last ${days}d`} value={compactNumber(overview?.totals?.new_users)} testid="ga4-new-users" />
            <MetricCard icon={Activity} label="Engagement rate" value={overview?.totals ? formatPercent(overview.totals.engagement_rate) : "-"} sub="Share of engaged sessions" testid="ga4-engagement-rate" />
            <MetricCard icon={Timer} label="Avg. session duration" value={overview?.totals ? formatDuration(overview.totals.avg_session_duration) : "-"} testid="ga4-avg-duration" />
          </div>

          <section className={`${panelClass} p-5`}>
            <h3 className="font-display text-lg text-white">Traffic over time</h3>
            <div className="mt-5 h-72">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={overview?.series || []}>
                  <defs>
                    <linearGradient id="usersFill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#9D00FF" stopOpacity={0.35} />
                      <stop offset="100%" stopColor="#9D00FF" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid stroke={chartGrid} vertical={false} />
                  <XAxis dataKey="date" tickFormatter={(d) => shortDate(d)} stroke={chartLine} fontSize={11} tickLine={false} />
                  <YAxis allowDecimals={false} stroke={chartLine} fontSize={11} tickLine={false} axisLine={false} />
                  <Tooltip {...chartTooltip} labelFormatter={(d) => shortDate(d)} />
                  <Area type="monotone" dataKey="users" name="Users" stroke="#9D00FF" strokeWidth={2} fill="url(#usersFill)" />
                  <Area type="monotone" dataKey="new_users" name="New users" stroke="#22d3ee" strokeWidth={2} fill="none" strokeDasharray="4 3" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </section>

          <div className="grid gap-4 xl:grid-cols-2">
            <RankedList title="Top pages" items={topPagesRanked} valueLabel="views" emptyLabel="Page views will appear here." />
            <RankedList title="Traffic sources" items={sourcesRanked} valueLabel="sessions" emptyLabel="Traffic sources will appear here." />
          </div>

          <div className="grid gap-4 xl:grid-cols-2">
            <section className={`${panelClass} p-5`}>
              <div className="flex items-center gap-2">
                <MonitorSmartphone size={16} className="text-[#c66bff]" />
                <h3 className="font-display text-lg text-white">Devices</h3>
              </div>
              <div className="mt-5 h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={deviceData} layout="vertical" margin={{ left: 12 }}>
                    <CartesianGrid stroke={chartGrid} horizontal={false} />
                    <XAxis type="number" allowDecimals={false} stroke={chartLine} fontSize={11} tickLine={false} />
                    <YAxis type="category" dataKey="name" width={78} stroke={chartLine} fontSize={11} tickLine={false} />
                    <Tooltip {...chartTooltip} />
                    <Bar dataKey="value" name="Users" fill="#7F00FF" radius={[0, 6, 6, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </section>
            <RankedList title="Top countries" items={geoRanked} valueLabel="users" emptyLabel="Geographic data will appear here." />
          </div>

          <RankedList title="Visitors by state (India)" items={regionsRanked} valueLabel="users" emptyLabel="State-level data will appear here." />

          {!!realtime?.top_screens?.length && (
            <section className={`${panelClass} p-5`}>
              <div className="flex items-center gap-2">
                <Globe size={16} className="text-[#c66bff]" />
                <h3 className="font-display text-lg text-white">Active right now, by page</h3>
              </div>
              <div className="mt-4 grid gap-2 sm:grid-cols-2">
                {realtime.top_screens.map((s) => (
                  <div key={s.name} className="flex items-center justify-between gap-3 rounded-xl border border-white/5 bg-white/[0.025] px-4 py-3">
                    <span className="truncate text-sm text-white/75">{s.name}</span>
                    <span className="shrink-0 text-xs tabular-nums text-white/45">{compactNumber(s.active_users)}</span>
                  </div>
                ))}
              </div>
            </section>
          )}
        </>
      )}
    </div>
  );
}
